// Copyright 2025, University of Maryland, All Rights Reserved
import { type WorkspaceBillAction } from '@/types/workspace-bill'
import { Button } from "@/components/ui/button"
import { Printer, FileText, Loader2 } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface WorkspaceBillsBulkActionBarProps {
  selected_count: number
  on_action: (action: WorkspaceBillAction) => void
  is_processing?: boolean
  all_selected_have_reviews?: boolean
  on_bulk_print?: () => void
}

export function WorkspaceBillsBulkActionBar({
  selected_count,
  on_action,
  is_processing = false,
  all_selected_have_reviews = true,
  on_bulk_print
}: WorkspaceBillsBulkActionBarProps) {
  if (selected_count === 0) {
    return (
      <div className="w-full">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2 w-full justify-between">
              <span>Bulk Actions</span>
              <span className="text-xs text-muted-foreground">
                No bills selected
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem className="text-muted-foreground text-center" disabled>
              Select bills to perform actions
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )
  }

  return (
    <div className="bg-primary/5 border border-primary/20 rounded-lg p-2 flex flex-col xs:flex-row items-start xs:items-center justify-between gap-2">
      <div className="px-2 xs:px-4 py-1">
        <span className="text-sm font-medium">
          {selected_count} bill{selected_count !== 1 ? 's' : ''} selected
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 w-full xs:w-auto justify-end px-2">
        <Button 
          size="sm" 
          variant="outline"
          onClick={() => on_action('LegislativeReview')} 
          className="gap-1 whitespace-nowrap flex-shrink-0"
          disabled={is_processing}
        >
          {is_processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          AI Legislative Review
        </Button>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>  {/* Wrapper div to allow tooltip on disabled button */}
                <Button 
                  size="sm" 
                  variant="outline"
                  className="gap-1 whitespace-nowrap flex-shrink-0" 
                  disabled={is_processing || !all_selected_have_reviews}
                  onClick={on_bulk_print}
                >
                  {is_processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                  Print Reviews
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {!all_selected_have_reviews 
                ? "Cannot print reviews: one or more selected bills don't have AI reviews generated" 
                : "Print reviews for all selected bills"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  )
}
